import { OpenFr8LogoSvg } from "~/components/assets/svg/OpenFr8LogoSvg";
import { Link, useLoaderData, useNavigation, useSearchParams, useSubmit } from "@remix-run/react";
import {
  ActionFunction,
  LoaderFunctionArgs,
  data,
  redirect,
} from "@remix-run/node";
import { STRATEGIES } from "~/.server/auth/auth";
import searchCity from "~/.server/indent/searchCity";
import { redirectIfAuthenticated, setSessionData } from '~/utils/auth/helper'
import { constants } from "~/utils/constants";
import { ButtonSubmit, Card, CardContent, InputController, toast, errorHandler, submitHandler, Clickable } from "@fr8/frui";
import { ChevronDown, EyeIcon, EyeOffIcon, LockKeyhole } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import CountryCodeSelect from "~/components/composed/common/countryCodeSelect";
import { Errors } from "~/components/composed/indent/createIndent/indentChunks";
import CampaignInit from "~/hooks/CampaignInit";
import PageLoader from "~/components/ui/custom_ui/loader";

function pickBestCityMatch(matches: Array<Record<string,string>>, searchedName: string) {
  if (!matches?.length) return undefined;
  const exact = matches.find(
    (c: Record<string,string>) => c.city_name.toLowerCase().trim() === searchedName.toLowerCase().trim()
  );
  return exact ?? matches[0];
}
import type { MetaFunction } from "@remix-run/node";

export const action: ActionFunction = async ({ request }) => {
  try {
    const { headers, company } = await setSessionData(request, STRATEGIES.CUSTOMER_LOGIN)
    const url = new URL(request.url);

    // ── Additive: carry booking prefill forward, safely, without touching normal flow ──
    if (company?.page) {
      try {
        const s_n = url.searchParams.get("s_n");
        const d_n = url.searchParams.get("d_n");

        if (s_n && d_n) {
          const [sourceMatches, destMatches] = await Promise.all([
            searchCity(s_n),
            searchCity(d_n),
          ]);

          const source = pickBestCityMatch(sourceMatches, s_n);
          const destination = pickBestCityMatch(destMatches, d_n);

          if (source && destination) {
            const prefillParams = new URLSearchParams({
              s_id: source.city_id,
              s_n: source.city_name,
              d_id: destination.city_id,
              d_n: destination.city_name,
            });
            return redirect(`${company.page}?${prefillParams.toString()}`, { headers });
          }
        } else {
          return redirect(company.page, { headers });
        }
      } catch {
        // Lookup failed — silently fall through to normal redirect below
      }
    }

    return redirect(constants.login, { headers });
  } catch (error) {
    if (error instanceof Response) return error;
    console.error("Callback Error:", error);
    const message = error instanceof Error ? error.message : "AUTH_FAILED";
    // Redirect to login with the error code in the URL
    return redirect(
      `${constants.login}?error=${encodeURIComponent(message)}`,
    );
  }
};

export async function loader({ request }: LoaderFunctionArgs) {
  const searchParams = new URLSearchParams(request.url.split("?")[1]);
  const campaignParam = searchParams.toString();
  const campaignData = campaignParam.includes("utm") ? campaignParam : "";
  const { getCommitHeaders, company } = await redirectIfAuthenticated(request);

  // ── Additive: carry booking prefill forward for already-logged-in users too ──
  const s_n = searchParams.get("s_n");
  const d_n = searchParams.get("d_n");

  if (s_n && d_n) {
    try {
      if (company?.page) {
        const [sourceMatches, destMatches] = await Promise.all([
          searchCity(s_n),
          searchCity(d_n),
        ]);

        const source = pickBestCityMatch(sourceMatches, s_n);
        const destination = pickBestCityMatch(destMatches, d_n);

        if (source && destination) {
          const prefillParams = new URLSearchParams({
            s_id: source.city_id,
            s_n: source.city_name,
            d_id: destination.city_id,
            d_n: destination.city_name,
          });
          throw redirect(`${company.page}?${prefillParams.toString()}`);
        }
      }
    } catch (err) {
      if (err instanceof Response) throw err;
      // Lookup failed — fall through to normal check below
    }
  }

  return data({ campaignData }, { headers: await getCommitHeaders() });
}

export const meta: MetaFunction = ({ location }) => {
  const searchParams = new URLSearchParams(location.search);
  const sourceName = searchParams.get("s_n");
  const destName = searchParams.get("d_n");

  if (sourceName && destName) {
    return [
      { title: `${sourceName} to ${destName} Truck Booking Login | Open FR8` },
      {
        name: "description",
        content: `Book trucks from ${sourceName} to ${destName} on Open FR8 — UAE's largest truck & load booking platform.`,
      },
      { name: "robots", content: "noindex, follow" },
    ];
  }

  return [
    { title: "Login | Open FR8" },
    {
      name: "description",
      content: "Sign in to Open FR8 — UAE's largest truck & load booking platform. Register and get trucks or loads within minutes.",
    },
  ];
};

export const links = () => [
  { rel: "canonical", href: "https://openfr8.com/uae/app/login" },
];

const loginWithPinFormSchema = z.object({
  mobile: z.string().refine((val) => /^\d+$/.test(val), {
    message: "Enter valid mobile number",
  }),
  password: z.coerce
    .number({
      invalid_type_error: "Invalid password",
    })
    .refine((val) => /^\d+$/.test(val.toString()), {
      message: "Enter valid password",
    }),
  country_id: z
    .number({
      message: "Country code is required",
    })
    .default(1),
  country_code: z
    .string({
      message: "Country code is required",
    })
    .default("971"),
});

const formDefaultValue = {
  mobile: "",
  password: undefined,
  country_id: 1,
  country_code: "971",
};

const GccLogin = () => {
  const { data: { campaignData } } = useLoaderData<typeof loader>();
  const [searchParams, setSearchParams] = useSearchParams();

  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";

  const submit = useSubmit();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showCountrySelectModal, setShowCountrySelectModal] =
    useState<boolean>();

  const form = useForm<z.infer<typeof loginWithPinFormSchema>>({
    defaultValues: formDefaultValue,
    mode: "onChange",
    reValidateMode: "onBlur",
    resolver: zodResolver(loginWithPinFormSchema),
  });

  // Set utm source params in local storage
  useEffect(() => {
    if (campaignData) {
      localStorage.setItem("utm_source", campaignData);
    }
  }, [campaignData]);

  const authError = searchParams.get("error");

  useEffect(() => {
    if (authError) {
      const errorMessage = authError;
  
      const timer = setTimeout(() => {
        toast.error(null, { description: errorMessage });
      }, 0);
  
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("error");
      setSearchParams(newParams, { replace: true });
  
      return () => clearTimeout(timer);
    }
  }, [authError, setSearchParams, searchParams]);

  type ValidationSchema = z.infer<typeof loginWithPinFormSchema>;
  const onSubmit = (values: ValidationSchema) => {
    submit(values, {
      method: "POST",
    });
  };

  const handleFormSubmit = submitHandler(form.handleSubmit, onSubmit, errorHandler());
  const toggleShowPassword = () => setShowPassword((prev) => !prev);

  const toggleCountryCode = () => setShowCountrySelectModal((prev) => !prev);
  const countryCodeWatcher = form.watch("country_code");
  const countryErrorMessage = form.formState.errors?.country_code?.message;
  return (
    <div className="flex min-h-screen items-center  w-full justify-center px-6">
      <div className="w-full flex flex-col max-w-md gap-4">
        <Card>
          <CardContent>
            <div className="flex flex-col">
              <OpenFr8LogoSvg className="h-24 mb-2 self-center" />
              <h1 className="font-medium text-lg text-start px-4">
              UAE&apos;s largest truck & load booking platform
             </h1>
              <p className="font-normal sm:text-sm text-xs text-start px-4 text-slate-400">
                Register and get truck or loads within minutes
              </p>
            </div>
          </CardContent>
        </Card>
          <form
            method="post"
            autoComplete="on"
            id="signInForm"
            className="flex flex-col justify-between max-h-max md:p-0 mt-4 gap-3"
            onSubmit={handleFormSubmit}
          >
            <p className="font-medium text-2xl">Sign in</p>
            <div>
              <Controller
                control={form.control}
                name="mobile"
                render={({ field, fieldState }) => (
                  <InputController
                    field={{ ...field, value: field.value ?? "" }}
                    fieldState={fieldState}
                    autoComplete="mobile tel"
                    type="tel"
                    maxLength={10}
                    className="ml-6"
                    prefixIcon={
                      <Clickable
                        onClick={toggleCountryCode}
                        className="ml-6 flex flex-row items-center cursor-pointer text-neutral-500 hover:font-bold"
                      >
                        <p>+{countryCodeWatcher}</p>
                        <ChevronDown size={16} />
                      </Clickable>
                    }
                    required
                    placeholder="Enter Mobile Number"
                    containerStyle="h-12"
                  />
                )}
              />
              {form.formState.errors.mobile ? null : (
                <Errors error={countryErrorMessage} />
              )}
            </div>
            <Controller
              control={form.control}
              name="password"
              render={({ field, fieldState }) => (
                <InputController
                  field={{ ...field, value: field.value ?? "" }}
                  fieldState={fieldState}
                  autoComplete="off"
                  type={showPassword ? "text" : "password"}
                  maxLength={4}
                  prefixIcon={<LockKeyhole size={18} className="text-gray-500" />}
                  required
                  placeholder="Enter pin"
                  containerStyle="h-12"
                  inputMode="numeric"
                  suffixIcon={
                    showPassword ? (
                      <EyeIcon size={18} onClick={toggleShowPassword} />
                    ) : (
                      <EyeOffIcon onClick={toggleShowPassword} size={18} />
                    )
                  }
                />
              )}
            />
            <Link
              to={constants.forgotPassword}
              className="text-right ml-auto text-primary cursor-pointer w-fit"
            >
              Forgot pin ?
            </Link>
            <ButtonSubmit
              isLoading={form.formState.isSubmitting}
            >
              Continue
            </ButtonSubmit>
            <Link
              className="cursor-pointer text-center"
              to={`${constants.createAccount}${campaignData ? `?${campaignData}` : ""}`}
            >
              No account ?{" "}
              <span className="underline font-semibold text-primary">
                Sign up
              </span>
            </Link>
            {showCountrySelectModal ? (
              <CountryCodeSelect
                onSelectCountryCode={(value) => {
                  form.setValue("country_id", value.country_id);
                  form.clearErrors("country_code");
                }}
                onClose={toggleCountryCode}
                selectedValue={form.getValues("country_code")}
                open={showCountrySelectModal}
              />
            ) : null}
          </form>
      </div>
      <CampaignInit />
      {isSubmitting && <PageLoader />}
    </div>
  );
};

export default GccLogin;
